/*
 * Homely remedies on the resident's screen: what the GP has agreed for them, whether it can be
 * given right now, and the warning when they are already prescribed something with the same
 * ingredient in it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import { Overlay } from './overlay.tsx';

type Remedy = {
  remedyId: string;
  name: string;
  forWhat: string;
  doseText: string;
  approved: boolean;
  approvedBy: string;
  approvedOn: string | null;
  given24h: number;
  lastGivenAt: string | null;
  daysRunning: number;
  blockedReason: string | null;
  clash: string | null;
};

type Given = {
  id: string;
  name: string;
  reason: string;
  givenAt: string;
  givenBy: string;
  worked: boolean | null;
};

export function HomelySection({ residentId }: { residentId: string }) {
  const [remedies, setRemedies] = useState<Remedy[] | null>(null);
  const [recent, setRecent] = useState<Given[]>([]);
  const [giving, setGiving] = useState<Remedy | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ remedies: Remedy[]; recent: Given[] }>(`/api/residents/${residentId}/homely`);
      setRemedies(data.remedies);
      setRecent(data.recent);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the homely remedies.');
    }
  }, [residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const approved = (remedies ?? []).filter((remedy) => remedy.approved);
  if (!approved.length && !recent.length) return null;

  const follow = recent.filter((row) => row.worked === null);

  return (
    <>
      <h2>From the cupboard</h2>
      <p className="muted small">
        Homely remedies are not prescribed. They can only be given to residents the GP has agreed
        each one for, and only within the limits the home agreed with them.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {follow.map((row) => (
        <p className="notice" key={row.id}>
          {row.name} given {formatWhen(row.givenAt)} for {row.reason.toLowerCase()}. Did it help?{' '}
          <Outcome id={row.id} onDone={() => void load()} />
        </p>
      ))}

      <ul className="list">
        {approved.map((remedy) => (
          <li key={remedy.remedyId}>
            <div className="row static column dose">
              <div className="row-line">
                <span className="row-main">{remedy.name}</span>
                <span className="time">{remedy.doseText}</span>
              </div>
              <p className="muted small">For {remedy.forWhat.toLowerCase()}</p>

              <div className="chips">
                <span className="chip">
                  GP: {remedy.approvedBy || 'approved'}{remedy.approvedOn ? `, ${formatDate(remedy.approvedOn)}` : ''}
                </span>
                {remedy.given24h ? <span className="chip">{remedy.given24h} given in 24 hours</span> : null}
                {remedy.daysRunning ? (
                  <span className={`chip ${remedy.daysRunning >= 2 ? 'warn' : ''}`}>
                    {remedy.daysRunning} day{remedy.daysRunning === 1 ? '' : 's'} running
                  </span>
                ) : null}
              </div>

              {remedy.clash ? <p className="notice warn">{remedy.clash}</p> : null}
              {remedy.blockedReason ? <p className="notice warn">{remedy.blockedReason}</p> : null}

              <div className="row-actions">
                <button type="button" disabled={!!remedy.blockedReason} onClick={() => setGiving(remedy)}>
                  Give this
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {giving ? (
        <GiveDialog
          remedy={giving}
          residentId={residentId}
          onClose={() => setGiving(null)}
          onDone={() => {
            setGiving(null);
            void load();
          }}
        />
      ) : null}
    </>
  );
}

function Outcome({ id, onDone }: { id: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const answer = async (worked: boolean) => {
    setBusy(true);
    try {
      await api.post(`/api/homely/${id}/outcome`, { worked });
      onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="row-actions">
      <button type="button" className="quiet" disabled={busy} onClick={() => void answer(true)}>Yes</button>
      <button type="button" className="quiet" disabled={busy} onClick={() => void answer(false)}>No</button>
    </span>
  );
}

function GiveDialog({
  remedy,
  residentId,
  onClose,
  onDone,
}: {
  remedy: Remedy;
  residentId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [agreeClash, setAgreeClash] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ daysRunning: number; maxDays: number } | null>(null);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        const result = await api.post<{ daysRunning: number; maxDays: number }>('/api/homely/give', {
          remedyId: remedy.remedyId,
          residentId,
          reason,
          note,
          agreeClash,
        });
        setDone(result);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [remedy, residentId, reason, note, agreeClash],
  );

  if (done) {
    return (
      <Overlay onClose={onDone}>
        <div className="card wide">
          <h2>Recorded</h2>
          <p className="muted">{remedy.name} for {reason.toLowerCase()}.</p>
          {done.daysRunning >= done.maxDays ? (
            <p className="notice warn">
              That is {done.daysRunning} days running. If it is needed again, ring the GP rather than giving more.
            </p>
          ) : (
            <p className="muted">Come back in an hour or so and say whether it helped.</p>
          )}
          <button type="button" onClick={onDone}>Done</button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{remedy.name}</h2>
        <p className="muted">{remedy.doseText} · for {remedy.forWhat.toLowerCase()}</p>

        {remedy.clash ? (
          <>
            <p className="notice warn">{remedy.clash}</p>
            <label className="check">
              <input type="checkbox" checked={agreeClash} onChange={(e) => setAgreeClash(e.target.checked)} />
              <span>I have checked with the nurse or the GP and they are happy for this to be given.</span>
            </label>
          </>
        ) : null}

        <label htmlFor="homely-reason">What is it needed for?</label>
        <input id="homely-reason" required value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Headache since lunchtime" />

        <label htmlFor="homely-note">Note (optional)</label>
        <input id="homely-note" value={note} onChange={(e) => setNote(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy || (!!remedy.clash && !agreeClash)}>
            {busy ? 'Saving…' : 'Record it as given'}
          </button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function formatDate(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `at ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
